import { createContext, useContext } from "react";
import { Transaction } from "@common/types/transaction";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import { TaxQuestion, TaxRule } from "@common/taxSavingRules";
import { TransactionDirectionType } from "@common/categories";
import { noop, noopAsync } from "@common/helpers/function";
import { SelectedAnswer, TaxAnswers } from "../TaxQuestions/types";
import { BulkUpdateResult } from "../PaginatedTransactionsTable.context";
import { TransactionsFilters } from "../filters/lib";
import { BulkUpdateDetails, BulkUpdateParams } from "./lib";

export type SelectedTransactionBase = Pick<
    Transaction,
    | "id"
    | "type"
    | "isBusiness"
    | "categoryId"
    | "isIgnored"
    | "memo"
    | "taxQuestionAnswers"
    | "entity"
    | "classAssignments"
    | "counterpartyId"
    | "customerId"
    | "pairedTransferId"
    | "splitParentId"
    | "amount"
    | "date"
    | "bankDescription"
    | "financialAccount"
    | "accountId"
    | "accrualAccountId"
    | "excludeFrom1099"
>;

export type SelectedTransaction = TransactionListItem & {
    transactionDirection: TransactionDirectionType;
};

export interface UpdateSelectedOptions {
    // Restricts the update to this subset of the current selection
    // (matched by transaction id), instead of every selected/filtered
    // transaction. Used by bulk actions whose target field can be locked
    // per-row (class, counterparty — see MatchedTransactionFieldLockedError):
    // the caller partitions the selection ahead of time (see
    // partitionSelectionByInvoiceOrBillLock) and only sends the eligible ids,
    // so a mixed selection applies to the unlocked rows instead of failing
    // the whole batch. Ignored in "select all by filters" mode — there is no
    // loaded row list to restrict there (mirrors the same posture already
    // used for the invoice/customer lock checks).
    targetTransactionIds?: number[];
}

export interface TransactionsBulkActionsContextValue {
    enabled: boolean;
    select(
        transactionOrTransactions: TransactionListItem | TransactionListItem[],
    ): void;
    deselect(
        transactionOrTransactions: TransactionListItem | TransactionListItem[],
    ): void;
    clearSelection(): void;
    isSelected(transaction: TransactionListItem): boolean;
    updateSelected(
        params: BulkUpdateParams,
        options?: UpdateSelectedOptions,
    ): Promise<BulkUpdateResult | undefined>;
    selected: Record<number, SelectedTransaction>;
    selectedArray: SelectedTransaction[];
    selectedCount: number;
    updateDetails: BulkUpdateDetails | null;
    subscribeToSelectionChanges(fn: () => void): () => void;
    taxRule: TaxRule | null;
    taxQuestions: TaxQuestion[];
    answers: TaxAnswers;
    handleAnswer(
        answers: SelectedAnswer[],
    ): Promise<BulkUpdateResult | undefined>;

    selectAll(): void;
    hasSelectedAll: boolean;
    filtersSelection: TransactionsFilters | null;
    totalTransactions: number;
}

const transactionsBulkActionsContext =
    createContext<TransactionsBulkActionsContextValue>({
        enabled: false,
        select: noop,
        deselect: noop,
        clearSelection: noop,
        isSelected: () => false,
        updateSelected: noopAsync,
        selected: {},
        selectedArray: [],
        selectedCount: 0,
        updateDetails: null,
        subscribeToSelectionChanges: () => noop,
        taxRule: null,
        taxQuestions: [],
        answers: {} as TaxAnswers,
        handleAnswer: noopAsync,
        selectAll: noop,
        hasSelectedAll: false,
        filtersSelection: null,
        totalTransactions: 0,
    });

export const TransactionsBulkActionsContextProvider =
    transactionsBulkActionsContext.Provider;

export function useTransactionsBulkActions() {
    return useContext(transactionsBulkActionsContext);
}
